// src/pages/AccountsPage.js

import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { alpha, useTheme } from '@mui/material/styles';

import {
  Add as AddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Key as KeyIcon,
  Lock as LockIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Pin as PinIcon,
  Search as SearchIcon,
  Work as WorkIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const ACCOUNT_ENDPOINT = '/accounts';

// ─────────────────────────────────────
// Helpers
// ─────────────────────────────────────

const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@$#%';
  let out = '';

  if (window.crypto?.getRandomValues) {
    const arr = new Uint32Array(14);
    window.crypto.getRandomValues(arr);

    for (let i = 0; i < arr.length; i += 1) {
      out += chars[arr[i] % chars.length];
    }

    return out;
  }

  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
};

const generatePin = () => String(Math.floor(1000 + Math.random() * 9000));

const isValidEmail = value =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

function cleanRutCompact(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/-/g, '')
    .replace(/\s+/g, '')
    .replace(/[^0-9K]/g, '');
}

function calculateRutDv(bodyDigits) {
  let sum = 0;
  let multiplier = 2;

  for (let i = bodyDigits.length - 1; i >= 0; i -= 1) {
    sum += Number(bodyDigits[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const mod = 11 - (sum % 11);

  if (mod === 11) return '0';
  if (mod === 10) return 'K';

  return String(mod);
}

function formatRutPretty(value) {
  const compact = cleanRutCompact(value);

  if (!compact || compact.length < 2) return String(value || '');

  const body = compact.slice(0, -1).replace(/^0+/, '') || '0';
  const dv = compact.slice(-1);

  const reversed = body.split('').reverse();
  const groups = [];

  for (let i = 0; i < reversed.length; i += 3) {
    groups.push(reversed.slice(i, i + 3).reverse().join(''));
  }

  return `${groups.reverse().join('.')}-${dv}`;
}

function normalizeRut(value) {
  const compact = cleanRutCompact(value);

  if (!compact || compact.length < 2) {
    return {
      ok: false,
      canonical: '',
      pretty: '',
      error: 'El RUT es obligatorio.'
    };
  }

  const bodyRaw = compact.slice(0, -1);
  const dv = compact.slice(-1);

  if (!/^\d+$/.test(bodyRaw)) {
    return {
      ok: false,
      canonical: '',
      pretty: '',
      error: 'El cuerpo del RUT debe contener sólo números.'
    };
  }

  if (!/^[0-9K]$/.test(dv)) {
    return {
      ok: false,
      canonical: '',
      pretty: '',
      error: 'Dígito verificador inválido.'
    };
  }

  const body = bodyRaw.replace(/^0+/, '') || '0';
  const expectedDv = calculateRutDv(body);

  if (expectedDv !== dv) {
    return {
      ok: false,
      canonical: `${body}-${dv}`,
      pretty: formatRutPretty(`${body}-${dv}`),
      error: `RUT inválido. El dígito verificador esperado es ${expectedDv}.`
    };
  }

  const canonical = `${body}-${dv}`;

  return {
    ok: true,
    canonical,
    pretty: formatRutPretty(canonical),
    error: ''
  };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function getFullName(acc) {
  const full = [
    acc?.nombres,
    acc?.apellido_paterno,
    acc?.apellido_materno
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return full || acc?.name || 'Usuario sin nombre';
}

function getInitials(acc) {
  const name = getFullName(acc);

  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'U';

  return parts
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
}

function formatDate(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
}

function roleLabel(roles) {
  const arr = safeArray(roles).filter(Boolean);

  if (!arr.length) return 'Sin rol';
  if (arr.length === 1) return arr[0];

  return `${arr[0]} +${arr.length - 1}`;
}

// ─────────────────────────────────────
// Componente principal
// ─────────────────────────────────────

export default function AccountsPage() {
  const theme = useTheme();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');

  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    nombres: '',
    apellido_paterno: '',
    apellido_materno: '',
    rut: '',
    email: '',
    telefono: '',
    cargo: '',
    observaciones: '',
    password: '',
    passwordConfirm: '',
    pin: '',
    status: 'activo'
  });

  const [pinMode, setPinMode] = useState('set');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const isEditing = Boolean(editingId);

  // ─────────────────────────────────────
  // Cargar cuentas
  // ─────────────────────────────────────

  const fetchAccounts = async () => {
    setLoading(true);

    try {
      const response = await api.get(ACCOUNT_ENDPOINT);
      setAccounts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar cuentas:', error);

      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Error al cargar cuentas',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // ─────────────────────────────────────
  // Derivados
  // ─────────────────────────────────────

  const stats = useMemo(() => {
    const total = accounts.length;
    const activos = accounts.filter(a => a.status === 'activo').length;
    const inactivos = accounts.filter(a => a.status !== 'activo').length;
    const conRut = accounts.filter(a => Boolean(a.rut)).length;
    const conCorreo = accounts.filter(a => Boolean(a.email)).length;
    const conPin = accounts.filter(a => Boolean(a.pin_configurado)).length;

    return {
      total,
      activos,
      inactivos,
      conRut,
      conCorreo,
      conPin
    };
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    const q = String(searchTerm || '').trim().toLowerCase();

    return accounts.filter(acc => {
      const matchesStatus =
        statusFilter === 'todos' ||
        String(acc.status || '').toLowerCase() === statusFilter;

      const prettyRut = acc.rut ? formatRutPretty(acc.rut) : '';

      const text = [
        acc.id,
        acc.name,
        acc.nombres,
        acc.apellido_paterno,
        acc.apellido_materno,
        prettyRut,
        acc.rut,
        acc.email,
        acc.telefono,
        acc.cargo,
        acc.observaciones,
        acc.status,
        safeArray(acc.roles).join(' '),
        safeArray(acc.tenants)
          .map(t => t.tenant_name || t.name || '')
          .join(' ')
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = !q || text.includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [accounts, searchTerm, statusFilter]);

  // ─────────────────────────────────────
  // Formulario
  // ─────────────────────────────────────

  const resetForm = () => {
    setFormData({
      nombres: '',
      apellido_paterno: '',
      apellido_materno: '',
      rut: '',
      email: '',
      telefono: '',
      cargo: '',
      observaciones: '',
      password: '',
      passwordConfirm: '',
      pin: '',
      status: 'activo'
    });

    setPinMode('set');
    setShowPassword(false);
  };

  const handleOpenForm = (account = null) => {
    if (account) {
      setEditingId(account.id);

      setFormData({
        nombres: account.nombres || '',
        apellido_paterno: account.apellido_paterno || '',
        apellido_materno: account.apellido_materno || '',
        rut: account.rut ? formatRutPretty(account.rut) : '',
        email: account.email || '',
        telefono: account.telefono || '',
        cargo: account.cargo || '',
        observaciones: account.observaciones || '',
        password: '',
        passwordConfirm: '',
        pin: '',
        status: account.status || 'activo'
      });

      setPinMode('keep');
      setShowPassword(false);
    } else {
      setEditingId(null);
      resetForm();
    }

    setOpenForm(true);
  };

  const handleCloseForm = () => {
    if (saving) return;
    setOpenForm(false);
  };

  const handleChange = e => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRutBlur = () => {
    const parsed = normalizeRut(formData.rut);

    if (parsed.ok) {
      setFormData(prev => ({
        ...prev,
        rut: parsed.pretty
      }));
    }
  };

  const handleChangePinMode = e => {
    const value = e.target.value;

    setPinMode(value);

    if (value !== 'set') {
      setFormData(prev => ({
        ...prev,
        pin: ''
      }));
    }
  };

  const handleGeneratePassword = () => {
    const pwd = generatePassword();

    setFormData(prev => ({
      ...prev,
      password: pwd,
      passwordConfirm: pwd
    }));

    setShowPassword(true);
  };

  const handleGeneratePin = () => {
    const p = generatePin();

    setFormData(prev => ({
      ...prev,
      pin: p
    }));

    setPinMode('set');
  };

  const validateForm = () => {
    const nombres = String(formData.nombres || '').trim();
    const apellidoPaterno = String(formData.apellido_paterno || '').trim();
    const apellidoMaterno = String(formData.apellido_materno || '').trim();

    const rutRaw = String(formData.rut || '').trim();
    const email = String(formData.email || '').trim().toLowerCase();
    const telefono = String(formData.telefono || '').trim();
    const cargo = String(formData.cargo || '').trim();
    const observaciones = String(formData.observaciones || '').trim();

    if (!nombres) {
      throw new Error('Los nombres son requeridos.');
    }

    const parsedRut = normalizeRut(rutRaw);

    if (!parsedRut.ok) {
      throw new Error(parsedRut.error || 'El RUT ingresado no es válido.');
    }

    if (email && !isValidEmail(email)) {
      throw new Error('El correo electrónico ingresado no es válido.');
    }

    if (!isEditing && !formData.password) {
      throw new Error('La contraseña es requerida para crear una cuenta.');
    }

    if (formData.password) {
      if (formData.password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres.');
      }

      if (formData.password !== formData.passwordConfirm) {
        throw new Error('La confirmación de contraseña no coincide.');
      }
    }

    if (!isEditing) {
      if (formData.pin && String(formData.pin).length < 4) {
        throw new Error('El PIN debe tener al menos 4 caracteres.');
      }
    } else if (pinMode === 'set') {
      if (!formData.pin) {
        throw new Error('Debe ingresar un PIN o cambiar el modo a "Mantener" o "Borrar PIN".');
      }

      if (String(formData.pin).length < 4) {
        throw new Error('El PIN debe tener al menos 4 caracteres.');
      }
    }

    return {
      nombres,
      apellido_paterno: apellidoPaterno || null,
      apellido_materno: apellidoMaterno || null,
      rut: parsedRut.canonical,
      email: email || null,
      telefono: telefono || null,
      cargo: cargo || null,
      observaciones: observaciones || null
    };
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const validated = validateForm();

      const payload = {
        nombres: validated.nombres,
        apellido_paterno: validated.apellido_paterno,
        apellido_materno: validated.apellido_materno,
        rut: validated.rut,
        email: validated.email,
        telefono: validated.telefono,
        cargo: validated.cargo,
        observaciones: validated.observaciones,
        status: formData.status
      };

      if (!isEditing || formData.password) {
        payload.password = formData.password;
      }

      if (!isEditing) {
        if (formData.pin) {
          payload.pin = formData.pin;
        }
      } else {
        if (pinMode === 'set') {
          payload.pin = formData.pin;
        } else if (pinMode === 'clear') {
          payload.pin = '';
        }
      }

      if (isEditing) {
        await api.put(`${ACCOUNT_ENDPOINT}/${editingId}`, payload);

        setSnackbar({
          open: true,
          message: 'Usuario actualizado correctamente',
          severity: 'success'
        });
      } else {
        await api.post(ACCOUNT_ENDPOINT, payload);

        setSnackbar({
          open: true,
          message: 'Usuario creado correctamente',
          severity: 'success'
        });
      }

      setOpenForm(false);
      fetchAccounts();
    } catch (error) {
      console.error('Error al guardar cuenta:', error);

      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.map(e => e.msg).join(' | ')
          : error.message || 'Error al guardar cuenta');

      setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Confirma eliminación de la cuenta?')) return;

    try {
      await api.delete(`${ACCOUNT_ENDPOINT}/${id}`);

      setSnackbar({
        open: true,
        message: 'Usuario eliminado',
        severity: 'info'
      });

      fetchAccounts();
    } catch (error) {
      console.error('Error al eliminar cuenta:', error);

      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Error al eliminar cuenta',
        severity: 'error'
      });
    }
  };

  // ─────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────

  const renderStatCard = ({ title, value, icon, color = 'primary', helper }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette[color].main, 0.18)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.10)}, ${alpha(
          theme.palette[color].main,
          0.025
        )})`
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.6}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette[color].main,
              backgroundColor: alpha(theme.palette[color].main, 0.12)
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>
              {value}
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 750, color: 'text.primary', mt: 0.4 }}>
              {title}
            </Typography>

            {helper ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {helper}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // ─────────────────────────────────────
  // Render principal
  // ─────────────────────────────────────

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 3 },
        backgroundColor: theme.palette.mode === 'dark' ? 'background.default' : '#f6f8fb',
        minHeight: '100%',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      <Stack spacing={2.5} sx={{ width: '100%', maxWidth: '100%' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.08)',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)}, ${alpha(
              theme.palette.background.paper,
              0.96
            )})`
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  flex: '0 0 auto'
                }}
              >
                <AdminPanelSettingsIcon />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 950,
                    lineHeight: 1.1,
                    fontSize: { xs: 25, md: 34 }
                  }}
                >
                  Administración de usuarios
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Gestiona usuarios, RUT, correo opcional, datos internos, roles y tenants asignados.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                textTransform: 'none',
                width: { xs: '100%', md: 'auto' }
              }}
            >
              Nuevo usuario
            </Button>
          </Stack>
        </Paper>

        {/* Stats */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: 'Usuarios',
              value: stats.total,
              icon: <PersonIcon />,
              color: 'primary',
              helper: 'Total registrados'
            })}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: 'Activos',
              value: stats.activos,
              icon: <AdminPanelSettingsIcon />,
              color: 'success',
              helper: `${stats.inactivos} inactivos`
            })}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: 'Con RUT',
              value: stats.conRut,
              icon: <BadgeIcon />,
              color: 'info',
              helper: 'Identificador principal'
            })}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: 'Con PIN',
              value: stats.conPin,
              icon: <PinIcon />,
              color: 'warning',
              helper: `${stats.conCorreo} con correo`
            })}
          </Grid>
        </Grid>

        {/* Filtros + tabla compacta */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.08)',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <TextField
                size="small"
                placeholder="Buscar por nombre, RUT, correo, cargo, rol o tenant…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{
                  width: { xs: '100%', md: 520 },
                  maxWidth: '100%'
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <FormControl size="small" sx={{ width: { xs: '100%', md: 180 } }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  label="Estado"
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="activo">Activos</MenuItem>
                  <MenuItem value="inactivo">Inactivos</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Divider />

          {loading ? (
            <Box display="flex" justifyContent="center" py={7}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Table
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& th': {
                    fontWeight: 900,
                    color: 'text.secondary',
                    backgroundColor: alpha(theme.palette.primary.main, 0.035),
                    whiteSpace: 'nowrap'
                  },
                  '& td': {
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    verticalAlign: 'top'
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '34%' }}>Usuario</TableCell>
                    <TableCell sx={{ width: '24%' }}>Contacto</TableCell>
                    <TableCell sx={{ width: '18%' }}>Acceso</TableCell>
                    <TableCell sx={{ width: '16%' }}>Actividad</TableCell>
                    <TableCell sx={{ width: '8%' }} align="right">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredAccounts.map(acc => {
                    const roles = safeArray(acc.roles);
                    const tenants = safeArray(acc.tenants);
                    const tenantCount = Number(acc.tenant_count || tenants.length || 0);

                    return (
                      <TableRow key={acc.id} hover>
                        {/* Usuario */}
                        <TableCell>
                          <Stack direction="row" spacing={1.3} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                fontSize: 13,
                                fontWeight: 900,
                                flex: '0 0 auto',
                                bgcolor:
                                  acc.status === 'activo'
                                    ? alpha(theme.palette.primary.main, 0.16)
                                    : alpha(theme.palette.text.secondary, 0.12),
                                color:
                                  acc.status === 'activo'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary
                              }}
                            >
                              {getInitials(acc)}
                            </Avatar>

                            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 950,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={getFullName(acc)}
                              >
                                {getFullName(acc)}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                ID #{acc.id}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.7}
                                alignItems="center"
                                sx={{
                                  mt: 0.7,
                                  flexWrap: 'wrap',
                                  rowGap: 0.6,
                                  maxWidth: '100%'
                                }}
                              >
                                <Chip
                                  size="small"
                                  icon={<BadgeIcon />}
                                  label={acc.rut ? formatRutPretty(acc.rut) : 'Sin RUT'}
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 850,
                                    borderRadius: 1.5,
                                    maxWidth: '100%'
                                  }}
                                />

                                {acc.cargo ? (
                                  <Chip
                                    size="small"
                                    icon={<WorkIcon />}
                                    label={acc.cargo}
                                    variant="outlined"
                                    sx={{
                                      fontWeight: 800,
                                      borderRadius: 1.5,
                                      maxWidth: '100%',
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }
                                    }}
                                  />
                                ) : null}
                              </Stack>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: 'text.secondary',
                                  mt: 0.6,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={[
                                  acc.nombres,
                                  acc.apellido_paterno,
                                  acc.apellido_materno
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                {[acc.nombres, acc.apellido_paterno, acc.apellido_materno]
                                  .filter(Boolean)
                                  .join(' ') || 'Sin nombres separados'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Contacto */}
                        <TableCell>
                          <Stack spacing={0.4} sx={{ minWidth: 0 }}>
                            {acc.email ? (
                              <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
                                <EmailIcon
                                  sx={{
                                    fontSize: 15,
                                    color: 'text.secondary',
                                    flex: '0 0 auto'
                                  }}
                                />

                                <Typography
                                  variant="body2"
                                  title={acc.email}
                                  sx={{
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {acc.email}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                Sin correo
                              </Typography>
                            )}

                            {acc.telefono ? (
                              <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
                                <PhoneIcon
                                  sx={{
                                    fontSize: 15,
                                    color: 'text.secondary',
                                    flex: '0 0 auto'
                                  }}
                                />

                                <Typography
                                  variant="caption"
                                  title={acc.telefono}
                                  sx={{
                                    color: 'text.secondary',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {acc.telefono}
                                </Typography>
                              </Stack>
                            ) : null}

                            {acc.observaciones ? (
                              <Typography
                                variant="caption"
                                title={acc.observaciones}
                                sx={{
                                  color: 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {acc.observaciones}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>

                        {/* Acceso */}
                        <TableCell>
                          <Stack spacing={0.7} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <Chip
                              size="small"
                              icon={<BusinessIcon />}
                              label={`${tenantCount} tenant${tenantCount === 1 ? '' : 's'}`}
                              color={tenantCount ? 'primary' : 'default'}
                              variant={tenantCount ? 'filled' : 'outlined'}
                              sx={{
                                fontWeight: 850,
                                maxWidth: '100%'
                              }}
                            />

                            <Tooltip title={roles.join(', ') || 'Sin roles'}>
                              <Chip
                                size="small"
                                label={roleLabel(roles)}
                                variant="outlined"
                                sx={{
                                  fontWeight: 800,
                                  maxWidth: '100%',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }
                                }}
                              />
                            </Tooltip>

                            <Chip
                              size="small"
                              icon={<PinIcon />}
                              label={acc.pin_configurado ? 'PIN configurado' : 'Sin PIN'}
                              color={acc.pin_configurado ? 'info' : 'default'}
                              variant={acc.pin_configurado ? 'filled' : 'outlined'}
                              sx={{
                                fontWeight: 800,
                                maxWidth: '100%'
                              }}
                            />
                          </Stack>
                        </TableCell>

                        {/* Actividad */}
                        <TableCell>
                          <Stack spacing={0.7} alignItems="flex-start">
                            <Chip
                              size="small"
                              label={acc.status === 'activo' ? 'Activo' : 'Inactivo'}
                              color={acc.status === 'activo' ? 'success' : 'default'}
                              sx={{ fontWeight: 850 }}
                            />

                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Último acceso
                              </Typography>

                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {formatDate(acc.ultimo_login_at)}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Actualizado
                              </Typography>

                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {formatDate(acc.updated_at)}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Acciones */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Editar usuario">
                              <IconButton size="small" onClick={() => handleOpenForm(acc)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar usuario">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(acc.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!filteredAccounts.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box sx={{ py: 5 }}>
                          <Typography sx={{ fontWeight: 850 }}>
                            No hay usuarios para mostrar.
                          </Typography>

                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Ajusta los filtros o crea un nuevo usuario.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Stack>

      {/* Formulario creación / edición */}
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 2.3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(
              theme.palette.primary.main,
              0.03
            )})`
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.13)
                }}
              >
                {isEditing ? <EditIcon /> : <AddIcon />}
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950 }}>
                  {isEditing ? 'Editar usuario' : 'Nuevo usuario'}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  El RUT es obligatorio. El correo es opcional.
                </Typography>
              </Box>
            </Stack>

            <IconButton onClick={handleCloseForm} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              El usuario podrá ingresar con su RUT y contraseña habitual. El correo queda como dato opcional para contacto o notificaciones.
            </Alert>

            {/* Identificación */}
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BadgeIcon color="primary" />

                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                        Identificación
                      </Typography>

                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Nombres, apellidos y RUT del usuario.
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Nombres"
                        name="nombres"
                        value={formData.nombres}
                        onChange={handleChange}
                        required
                        disabled={saving}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Apellido paterno"
                        name="apellido_paterno"
                        value={formData.apellido_paterno}
                        onChange={handleChange}
                        disabled={saving}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Apellido materno"
                        name="apellido_materno"
                        value={formData.apellido_materno}
                        onChange={handleChange}
                        disabled={saving}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="RUT"
                        name="rut"
                        value={formData.rut}
                        onChange={handleChange}
                        onBlur={handleRutBlur}
                        required
                        disabled={saving}
                        placeholder="16.717.958-4"
                        helperText="Obligatorio. Se guarda normalizado y permite login con RUT."
                        inputProps={{
                          maxLength: 12,
                          autoComplete: 'off',
                          autoCapitalize: 'characters'
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Correo electrónico"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={saving}
                        placeholder="usuario@empresa.cl"
                        helperText="Opcional. Puede quedar vacío si el usuario sólo ingresará con RUT."
                        inputProps={{
                          autoComplete: 'email',
                          autoCapitalize: 'none'
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            {/* Datos internos */}
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WorkIcon color="primary" />

                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                        Datos internos
                      </Typography>

                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Información adicional para administración.
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Teléfono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        disabled={saving}
                        placeholder="+56 9 1234 5678"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Cargo"
                        name="cargo"
                        value={formData.cargo}
                        onChange={handleChange}
                        disabled={saving}
                        placeholder="Ej: Vendedor, Supervisor, Administrador"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WorkIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Observaciones"
                        name="observaciones"
                        value={formData.observaciones}
                        onChange={handleChange}
                        disabled={saving}
                        placeholder="Notas internas del usuario"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <NotesIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            {/* Acceso */}
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <KeyIcon color="primary" />

                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                        Credenciales de acceso
                      </Typography>

                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Contraseña de ingreso y PIN para autorizaciones sensibles.
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          fullWidth
                          label={isEditing ? 'Nueva contraseña' : 'Contraseña'}
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          required={!isEditing}
                          disabled={saving}
                          helperText={
                            isEditing
                              ? 'Opcional. Sólo completa si deseas cambiarla.'
                              : 'Mínimo 6 caracteres.'
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowPassword(prev => !prev)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />

                        <Button
                          variant="outlined"
                          onClick={handleGeneratePassword}
                          disabled={saving}
                          sx={{
                            minWidth: 105,
                            fontWeight: 850,
                            textTransform: 'none'
                          }}
                        >
                          Generar
                        </Button>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Confirmar contraseña"
                        type={showPassword ? 'text' : 'password'}
                        name="passwordConfirm"
                        value={formData.passwordConfirm}
                        onChange={handleChange}
                        required={!isEditing || !!formData.password}
                        disabled={saving}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>

                    {isEditing && (
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Modo PIN</InputLabel>
                          <Select
                            value={pinMode}
                            label="Modo PIN"
                            onChange={handleChangePinMode}
                            disabled={saving}
                          >
                            <MenuItem value="keep">Mantener PIN actual</MenuItem>
                            <MenuItem value="set">Establecer / Cambiar PIN</MenuItem>
                            <MenuItem value="clear">Borrar PIN</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {(pinMode === 'set' || !isEditing) && (
                      <Grid item xs={12} md={isEditing ? 8 : 12}>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            fullWidth
                            label={isEditing ? 'Nuevo PIN' : 'PIN'}
                            name="pin"
                            value={formData.pin}
                            onChange={handleChange}
                            disabled={saving}
                            helperText="Opcional al crear. Entre 4 y 12 caracteres. Se cifra en el servidor."
                            inputProps={{
                              maxLength: 12,
                              autoComplete: 'off'
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PinIcon fontSize="small" />
                                </InputAdornment>
                              )
                            }}
                          />

                          <Button
                            variant="outlined"
                            onClick={handleGeneratePin}
                            disabled={saving}
                            sx={{
                              minWidth: 105,
                              fontWeight: 850,
                              textTransform: 'none'
                            }}
                          >
                            Generar
                          </Button>
                        </Stack>
                      </Grid>
                    )}

                    {isEditing && pinMode === 'clear' && (
                      <Grid item xs={12}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          Al guardar, el PIN será eliminado para esta cuenta.
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            {/* Estado */}
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                        Estado de la cuenta
                      </Typography>

                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Un usuario inactivo no debería poder iniciar sesión.
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        name="status"
                        value={formData.status}
                        label="Estado"
                        onChange={handleChange}
                        disabled={saving}
                      >
                        <MenuItem value="activo">Activo</MenuItem>
                        <MenuItem value="inactivo">Inactivo</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseForm}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 850
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              borderRadius: 2
            }}
          >
            {saving ? 'Guardando…' : isEditing ? 'Actualizar usuario' : 'Crear usuario'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar(prev => ({
            ...prev,
            open: false
          }))
        }
      >
        <Alert
          onClose={() =>
            setSnackbar(prev => ({
              ...prev,
              open: false
            }))
          }
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}